import { getApiEnvironmentTable } from '~/components/ContentTools/apiSkillEnvironments';

const OFFER_CONTENT_ENDPOINTS = [
  {
    label: 'Offer schema',
    method: 'OPTIONS',
    path: '/api/offers/:offerId',
    notes: 'Schema for the main offer editor. Use this to understand editable fields before PATCHing.',
  },
  {
    label: 'Offer details',
    method: 'GET',
    path: '/api/offers/:offerId?retain_dynamic_tags=true',
    notes: 'Current offer data for copy, content, images, videos, badges, packages, schedules, and status.',
  },
  {
    label: 'Update offer content',
    method: 'PATCH',
    path: '/api/offers/:offerId',
    notes:
      'Partial update for main offer copy and content fields, structured fine print, videos, and visibility flags.',
  },
  {
    label: 'Localised content',
    method: 'GET',
    path: '/api/offers/:offerId/localization',
    notes: 'Market-specific overrides for name, description, additional description, and highlights.',
  },
  {
    label: 'Update localised content',
    method: 'PUT',
    path: '/api/offers/:offerId/localization',
    notes: 'Updates the full `localizationStrings` array for market-specific content.',
  },
  {
    label: 'Offer templates',
    method: 'GET',
    path: '/api/offers/templates/all',
    notes: 'Approved templates used by content editors, especially location and fine-print templates.',
  },
  {
    label: 'Offer images',
    method: 'GET',
    path: '/api/offers/:offerId/images',
    notes: 'Current offer image list, ordering, captions, and metadata.',
  },
  {
    label: 'Add offer image',
    method: 'POST',
    path: '/api/offers/:offerId/images',
    notes:
      'Advanced: attaches a known Cloudinary image to the offer gallery. Use only when the content user provides exact asset details.',
  },
  {
    label: 'Update offer images',
    method: 'PATCH',
    path: '/api/offers/:offerId/images',
    notes: 'Bulk image ordering and metadata update.',
  },
  {
    label: 'Update one offer image',
    method: 'PATCH',
    path: '/api/offers/:offerId/images/:imageId',
    notes: 'Single image metadata update.',
  },
  {
    label: 'Traveller requirements',
    method: 'GET',
    path: '/api/traveller/traveller-requirements/:offerId',
    notes: 'Tour offer traveller requirements form data.',
  },
  {
    label: 'Traveller requirements schema',
    method: 'OPTIONS',
    path: '/api/traveller/traveller-requirements/:offerId',
    notes: 'Schema for tour traveller requirements.',
  },
  {
    label: 'Update traveller requirements',
    method: 'PUT',
    path: '/api/traveller/traveller-requirements/:offerId',
    notes: 'Replaces the traveller requirements form data for a tour offer.',
  },
  {
    label: 'Badges',
    method: 'GET',
    path: '/api/offers/badges/all',
    notes: 'Available marketing badges.',
  },
  {
    label: 'Attach badges',
    method: 'POST',
    path: '/api/offers/:offerId/badges',
    notes: 'Adds existing badges to an offer by badge ID.',
  },
  {
    label: 'Update attached badge',
    method: 'PATCH',
    path: '/api/offers/:offerId/badges/:offerBadgeId',
    notes: 'Updates badge regions, schedules, and brand targeting.',
  },
  {
    label: 'Remove attached badge',
    method: 'DELETE',
    path: '/api/offers/:offerId/badges/:offerBadgeId',
    notes: 'Removes a badge from an offer.',
  },
  {
    label: 'Generated offer content',
    method: 'GET',
    path: '/api/accommodation/admin/content/offers/:offerId',
    notes: 'Generated-content history for accommodation-backed offers. Use `LUX_API_BASE_URL` when provided.',
  },
  {
    label: 'Offer content prompt',
    method: 'GET',
    path: '/api/accommodation/admin/content/offers/get-prompt',
    notes: 'Prompt defaults used by the Admin generated-content tab. Use `LUX_API_BASE_URL` when provided.',
  },
  {
    label: 'Generate offer content',
    method: 'POST',
    path: '/api/accommodation/admin/content/offers/:offerId/generate-content',
    notes: 'Starts generated-content creation for accommodation-backed offers. Use `LUX_API_BASE_URL` when provided.',
  },
  {
    label: 'Package inclusions',
    method: 'GET',
    path: '/api/reservations/inclusions-catalogue/package-inclusions/:offerId',
    notes: 'Hotel package inclusion copy and ordering.',
  },
  {
    label: 'Update package inclusions',
    method: 'PUT',
    path: '/api/reservations/inclusions-catalogue/package-inclusions',
    notes: 'Updates package inclusion copy/order. Do not use for rates or inventory.',
  },
];

const OFFER_COPY_PATCH_FIELDS = [
  {
    area: 'Default offer content',
    fields:
      '`name`, `name_override_for_edm`, `location`, `slug`, `subject_line`, `preheader`, `description`, `additional_description`, `highlights`, `getting_there`, `fine_print`, `facilities`, `what_we_like`',
  },
  {
    area: 'Offer inclusions and tiles',
    fields:
      '`offer_inclusions_heading`, `offer_inclusions_long`, `offer_tile_inclusion_heading`, `offer_inclusions`, `offer_tile_inclusions`, `offer_inclusions_short`, `sale_unit`',
  },
  {
    area: 'Structured fine print',
    fields: '`structured_fine_print.cancellation_text`, `date_changes_text`, `inclusion_notes`, `fine_print`',
  },
  {
    area: 'Localised content',
    fields: '`localizationStrings[].countries`, `name`, `description`, `additional_description`, `highlights`',
  },
  {
    area: 'Marketing tags and visibility',
    fields:
      '`locations`, `holiday_types`, `campaigns`, `partnerships`, `no_index`, `is_search_by_name_hidden`, `is_search_hidden`, `is_discount_pill_hidden`',
  },
  {
    area: 'Media',
    fields:
      '`images` metadata/order/captions, `video_vimeo_id`, `whitelabel_video_vimeo_id`, `is_video_vimeo_hero`, `is_whitelabel_video_vimeo_hero`',
  },
  {
    area: 'Tour-related content',
    fields:
      '`traveller-requirements`, `tour_flight_details_required`, `start_location`, `end_location`, `vendor_name`, `vendor_vehicle`, `cabin_types`',
  },
  {
    area: 'Package inclusion catalogue',
    fields:
      'Package inclusion catalogue rows and package content ordering. Avoid package record endpoints and commercial fields.',
  },
];

const BROAD_COPY_FIRST_PASS_FIELDS = [
  '`name_override_for_edm`',
  '`subject_line`',
  '`preheader`',
  '`description`',
  '`additional_description`',
  '`highlights`',
  '`location`',
  '`getting_there`',
  '`fine_print`',
  '`facilities`',
  '`offer_inclusions_*`',
  '`what_we_like`',
].join(', ');

export function getOffersCodexSkillText() {
  const environmentTable = getApiEnvironmentTable();
  const endpointTable = OFFER_CONTENT_ENDPOINTS.map(
    ({ label, method, path, notes }) => `| ${label} | \`${method}\` | \`${path}\` | ${notes} |`,
  ).join('\n');
  const patchFieldsTable = OFFER_COPY_PATCH_FIELDS.map(({ area, fields }) => `| ${area} | ${fields} |`).join('\n');

  return `# Luxury Escapes Admin Offers API Editing Skill

Use this skill when a Luxury Escapes content user asks Codex to inspect or edit offer copy/content through the Admin Portal API instead of navigating the browser UI.

This skill is intentionally scoped to content-team workflows: copy, rich text, fine print, localisation, gallery metadata, videos, badges, traveller requirements, search visibility fields, generated content, and package inclusion catalogue copy. Do not use it for rates, rate plans, availability, inventory, payment schedules, package pricing, cancellation policy logic, vendor finance, or commercial configuration unless the user explicitly asks and confirms the exact endpoint.

## Inputs to ask the user for

- The API details block copied from the Admin Portal. It should include \`API_BASE_URL\`, \`LUX_API_BASE_URL\`, and \`ADMIN_BEARER_TOKEN\`.
- Which environment they want edited: \`test\` or \`prod\`.
- The offer ID, usually from an Admin URL like \`/offers/:offerId\` or \`/edit-offers/:offerId\`.
- The exact page/tab/field or content change requested.

Never ask the user to paste long-lived credentials. Never print the bearer token back to the user. Keep it only in memory for the current task.

## API hosts

Do not call the Admin Portal web-app host, such as \`https://test-admin.luxuryescapes.com\` or \`https://admin.luxuryescapes.com\`. The Admin Portal talks to separate API hosts. Prefer the copied values from the Admin Portal:

- Use \`API_BASE_URL\` for \`/api/offers\`, \`/api/traveller\`, and \`/api/reservations\` endpoints.
- Use \`LUX_API_BASE_URL\` for \`/api/accommodation/admin/content/offers\` generated-content endpoints. If it is missing, use \`API_BASE_URL\`.

If the user did not paste API details, choose the API base URL from the environment:

| Environment | API base URL | Use for |
| --- | --- | --- |
${environmentTable}

If the user is working from a review app or a local setup with a custom backend, ask them to use the Admin Portal's "Copy API details" button so you get the exact configured host values and a matching token.

## Authentication

Use the copied \`ADMIN_BEARER_TOKEN\` value. Send every API request with:

\`\`\`http
Authorization: Bearer <ADMIN_TOKEN>
Content-Type: application/json;charset=UTF-8
\`\`\`

## Endpoints

The base URL for most requests is:

\`\`\`text
<API_BASE_URL><URL path above>
\`\`\`

For generated-content endpoints, use:

\`\`\`text
<LUX_API_BASE_URL><URL path above>
\`\`\`

Replace \`:offerId\`, \`:imageId\`, \`:offerBadgeId\`, and \`:packageId\` with the IDs for the task.

| Area | Method | URL path | Use for |
| --- | --- | --- | --- |
${endpointTable}

If a request unexpectedly returns the Admin HTML shell instead of JSON, the host is wrong; switch from the Admin Portal web-app host to the copied API host for that environment.

## Common content fields

Fetch the current data first, then edit only the fields needed for the requested content change.

| Admin area | Common fields |
| --- | --- |
${patchFieldsTable}

## Fast field discovery

- Main offer copy is usually enough for broad copy changes.
- Fetch \`OPTIONS /api/offers/:offerId\` before \`PATCH /api/offers/:offerId\` because \`GET\` can include values that the PATCH schema rejects.
- Some fields may come back as \`null\` from GET but reject \`null\` on PATCH. Known examples are \`offer_inclusions_heading\`, \`offer_inclusions_long\`, and \`offer_tile_inclusions\`; use an empty string when clearing those fields.
- For broad copy rewrites, do not include \`slug\` unless the user explicitly asks. It has a strict pattern and affects URLs.
- Do not rewrite \`booking_guarantee\` by default. It is legal/standardised copy, not ordinary offer tone of voice copy.
- Treat these as first-pass fields for broad copy rewrites: ${BROAD_COPY_FIRST_PASS_FIELDS}.
- Save three local artifacts for every mutation: the full before snapshot, the exact requested patch, and a rollback patch for only the changed fields.
- On revert, verify field-by-field against the saved rollback, not just by checking that PATCH returned 200.

## Broad copy rewrite workflow

1. \`OPTIONS /api/offers/:offerId\`
2. \`GET /api/offers/:offerId?retain_dynamic_tags=true\`
3. Optional read-only checks: \`GET /api/offers/:offerId/localization\`, \`GET /api/offers/:offerId/images\`, and \`GET /api/reservations/inclusions-catalogue/package-inclusions/:offerId\`
4. Save a rollback payload for only the fields being changed.
5. \`PATCH /api/offers/:offerId\`
6. \`GET /api/offers/:offerId?retain_dynamic_tags=true\`
7. Verify the exact changed fields.

## Avoid for ordinary content work

- \`OPTIONS /api/offers/:offerId/packages\`
- \`PATCH /api/offers/:offerId/packages/:packageId\`

Package records mix copy with commercial fields, rate-plan-like fields, prices, dates, room policy data, occupancy, addons, upgrades, and package options. Do not use package record endpoints unless the user explicitly confirms that they want a package record edit and gives the exact field scope. Prefer the package inclusion catalogue endpoints for inclusion copy/order changes.

## Workflow

1. Identify the Admin page or content area that owns the requested field: main offer editor, structured fine print, localised content, images, videos, badges, traveller requirements, generated content, or package inclusions.
2. GET the current resource and, where available, OPTIONS the schema before editing.
3. Make the smallest possible JSON edit. Preserve unrelated IDs, ordering, booleans, arrays, market lists, image IDs, badge IDs, package IDs, and nested object structure.
4. Show the user a concise before/after summary before mutating, unless they already asked you to apply directly.
5. PATCH, PUT, or POST only the endpoint that owns the requested field.
6. GET the same resource again and verify the expected content is present.
7. If the user asks to test a change and revert it, capture the original value first, PATCH the test value, verify, then PATCH the original value back and verify again.

## Safety rules

- Prefer GET before PATCH/PUT/POST. Do not construct payloads from memory.
- Do not edit rates, rate plans, availability, inventory, payment schedules, package prices, booking windows, vendor payment fields, or cancellation-policy logic.
- Do not use offer package record endpoints, such as \`/api/offers/:offerId/packages\`, for ordinary content work. Use package inclusion catalogue endpoints only for inclusion copy/order.
- Image endpoints should be used for gallery metadata, order, captions, hero flags, or attaching known Cloudinary images only. Do not upload or replace image assets unless the user specifically asks.
- Badge endpoints should be used only for offer badge selection, regions, brand targeting, or schedules.
- Generated-content endpoints create suggestions; applying generated content to the live offer still requires a separate offer PATCH.
- If the schema or current data does not clearly identify the target field, stop and ask for the exact page/tab/field instead of guessing.
- If a mutation fails, report the status and response body without retrying destructive changes blindly.
`;
}
